import { useState, useEffect } from "react";
import { ShieldCheck, X } from "lucide-react";
import {
  getConsentDecision,
  grantTrackingConsent,
  revokeTrackingConsent,
} from "../../lib/metaPixel";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  useEffect(() => {
    // Check existing decision on mount
    const decision = getConsentDecision();
    if (!decision) {
      setVisible(true);
    }

    // Listen for footer "Cookie Settings" trigger to reopen banner
    const handleOpenSettings = () => {
      setVisible(true);
    };

    window.addEventListener("admall_open_cookie_settings", handleOpenSettings);
    return () => {
      window.removeEventListener("admall_open_cookie_settings", handleOpenSettings);
    };
  }, []);

  const handleAccept = () => {
    grantTrackingConsent();
    setVisible(false);
  };

  const handleReject = () => {
    revokeTrackingConsent();
    setVisible(false);
  };

  if (!visible && !showPrivacyModal) return null;

  return (
    <>
      {/* Cookie Consent Banner */}
      {visible && (
        <aside
          role="region"
          aria-label="Cookie Consent"
          data-testid="cookie-consent-banner"
          className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-[#0B132B] text-white shadow-2xl transition-all duration-300 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-xl sm:rounded-2xl sm:border sm:border-white/15"
        >
          <div className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 hidden rounded-lg bg-[#FF5722]/10 p-2 text-[#FF5722] sm:block">
                <ShieldCheck size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-base font-bold uppercase tracking-wider text-white">
                    Your Privacy Matters
                  </h3>
                  <button
                    type="button"
                    onClick={handleReject}
                    aria-label="Dismiss banner and reject non-essential cookies"
                    className="text-white/50 hover:text-white sm:hidden"
                  >
                    <X size={18} />
                  </button>
                </div>
                <p className="mt-1 font-body text-xs leading-relaxed text-white/80 sm:text-sm">
                  We use essential cookies to keep this website working. With your permission, we also use Meta Pixel to understand website activity and improve our advertising.
                </p>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 pt-1">
                  <button
                    type="button"
                    data-testid="cookie-privacy-link"
                    onClick={() => setShowPrivacyModal(true)}
                    className="font-body text-xs font-semibold text-white/70 underline underline-offset-2 transition hover:text-[#FF5722]"
                  >
                    Privacy Policy
                  </button>

                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                    <button
                      type="button"
                      data-testid="cookie-reject-btn"
                      onClick={handleReject}
                      className="w-full rounded-full border border-white/20 bg-transparent px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-white transition hover:border-white hover:bg-white/10 sm:w-auto"
                    >
                      Reject Non-Essential
                    </button>
                    <button
                      type="button"
                      data-testid="cookie-accept-btn"
                      onClick={handleAccept}
                      className="w-full rounded-full bg-[#FF5722] px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-white transition hover:bg-[#E64A19] sm:w-auto"
                    >
                      Accept All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* Lightweight Privacy Policy Modal */}
      {showPrivacyModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-modal-title"
          data-testid="cookie-privacy-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl text-navy"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h4 id="privacy-modal-title" className="font-display text-lg font-bold uppercase tracking-tight text-[#0B132B]">
                Privacy & Cookie Policy
              </h4>
              <button
                type="button"
                onClick={() => setShowPrivacyModal(false)}
                aria-label="Close privacy modal"
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-navy"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-4 space-y-3 font-body text-xs text-slate-600 sm:text-sm max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <h5 className="font-bold uppercase tracking-wider text-[#0B132B]">1. Essential Cookies</h5>
                <p className="mt-1">
                  These technical cookies are strictly necessary to provide core website functionality, ensure secure form delivery, and remember your privacy preferences. They do not track your personal identity.
                </p>
              </div>

              <div>
                <h5 className="font-bold uppercase tracking-wider text-[#0B132B]">2. Meta Pixel & Advertising</h5>
                <p className="mt-1">
                  With your explicit consent, we activate Meta Pixel (ID: 4381907418741692) to measure advertising effectiveness. We maintain a strict zero-PII policy: your name, mobile number, email, and private message contents are never transmitted to Meta.
                </p>
              </div>

              <div>
                <h5 className="font-bold uppercase tracking-wider text-[#0B132B]">3. Managing Your Choices</h5>
                <p className="mt-1">
                  You may change or revoke your cookie choices at any time by clicking the &ldquo;Cookie Settings&rdquo; link situated in the website footer.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() => setShowPrivacyModal(false)}
                className="rounded-full bg-[#0B132B] px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-white transition hover:bg-[#1C2541]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
